import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { getCurrentUser as getCognitoCurrentUser } from 'aws-amplify/auth'
import { usePostHog } from 'posthog-js/react'

import { useAuth } from '../../../libraries/auth'
import TextInput from '../../components/form/TextInput'
import Button from '../../components/form/Button'
import FormGroup from '../../components/form/FormGroup'
import ErrorMessage from '../../components/errors/ErrorMessage'
import { useI18N } from '../../../libraries/i18n/hook'
import PasswordInput from '../../components/form/PasswordInput'

const LoginForm = () => {
  const { login, checkIsAuthenticated, resendVerificationCode } = useAuth()
  const posthog = usePostHog()
  const { t } = useI18N()
  const [searchParams] = useSearchParams()
  const [username, setUsername] = useState(searchParams.get('username') || '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {

    e.preventDefault()
    setError('')
    setIsLoading(true)

    const isAuthenticated = await checkIsAuthenticated()
    if(isAuthenticated) {
      return navigate('/')
    }

    try {
      const result = await login(username, password)
      if (!result.success) {
        if (result.code === 'UserNotConfirmedException') {
          // User exists but isn't verified
          await resendVerificationCode(username)
          navigate(`/verify-email?username=${encodeURIComponent(username)}`)
          return
        }
        setError(result.error || 'Failed to login')
        return
      }
      const cognitoUser = await getCognitoCurrentUser()
      let email = cognitoUser?.signInDetails?.loginId
      // Fallback to username from the form if email is not available
      if (!email) {
        email = username
      }
      posthog.identify(cognitoUser?.userId?.toString(), { email: email })
      navigate('/')
    } catch (err) {
      setError(`An unexpected error occurred ${err}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className='w-full space-y-6'>
      {error && <ErrorMessage error={new Error(error)} />}

      <FormGroup>
        <TextInput
          value={username}
          label={t('auth.login.username')}
          placeholder={t('auth.login.username')}
          name='username'
          onChange={(e) => setUsername(e.target.value?.trim())}
          required
          className='w-full border border-gray-600'
          type='email'
        />
      </FormGroup>

      <FormGroup>
        <PasswordInput
          value={password}
          label={t('auth.login.password')}
          placeholder={t('auth.login.password')}
          name='password'
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div className='text-center mt-4'>
        <p className='text-xs text-gray-600'>
          {t('auth.login.forgotPassword')} {' '}
          <Link to='/reset-password' className='font-medium text-deep-green hover:text-gray-400'>
            {t('auth.login.resetPassword')}
          </Link>
        </p>
      </div>
      </FormGroup>



      <Button
        type='submit'
        variant='primary'
        size='md'
        loading={isLoading}
        disabled={isLoading}
        className='w-full'
      >
        {t('auth.login.signIn')}
      </Button>

    </form>
  )
}

export default LoginForm
